import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { partnerApi } from '@/api/endpoints'
import { useCouponMutation, useCoupons } from '@/api/queries'
import { LinkIcon, PlusIcon, TicketIcon } from '@/components/icons'
import { Button } from '@/components/ui/Button'
import { CopyButton, Switch, td, th } from '@/components/ui/Controls'
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback'
import { Modal } from '@/components/ui/Modal'
import { PageHeader } from '@/components/ui/Page'
import { COUPON_STATUS, Pill } from '@/components/ui/StatusPill'
import { describeError } from '@/lib/errors'
import { count, date, money, pct } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/store/auth'
import type { Coupon } from '@/types/api'
import { CouponDialog, cleanCode } from './CouponDialog'

export function CouponsPage() {
  const partner = useAuthStore((state) => state.partner)
  const query = useCoupons()
  const [params, setParams] = useSearchParams()
  const [editing, setEditing] = useState<Coupon | null>(null)
  const [deleting, setDeleting] = useState<Coupon | null>(null)
  const creating = params.get('new') === '1'

  const openCreate = () => setParams({ new: '1' }, { replace: true })
  const closeDialog = () => {
    setEditing(null)
    if (creating) setParams({}, { replace: true })
  }

  const coupons = query.data ?? []
  const maxPct = partner?.maxDiscountPct ?? 0
  const fallbackWord = cleanCode((partner?.company || partner?.name || 'JOBSLIDE').split(/\s+/)[0] ?? 'JOBSLIDE').slice(0, 12) || 'JOBSLIDE'

  return (
    <>
      <PageHeader
        title="Coupons"
        description={`Each code gives the customer part of your ${pct(maxPct)} maximum; you earn the rest on every payment it covers.`}
        actions={
          <Button size="sm" icon={<PlusIcon className="h-4 w-4" />} onClick={openCreate}>
            Create coupon
          </Button>
        }
      />

      {query.isError && !query.data ? (
        <div className="card">
          <ErrorState {...describeError(query.error, 'Could not load your coupons')} onRetry={() => void query.refetch()} />
        </div>
      ) : query.isLoading ? (
        <div className="card space-y-3 p-5">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-14" />
          ))}
        </div>
      ) : coupons.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={<TicketIcon className="h-8 w-8" />}
            title="Create your first coupon and share the link"
            description={`Pick a code such as IPHONE-${partner?.tag ?? 'P1'} and a discount up to ${pct(maxPct)}. Share the link; everyone who pays with it is onboarded to you.`}
            action={
              <Button icon={<PlusIcon className="h-4 w-4" />} onClick={openCreate}>
                Create coupon
              </Button>
            }
          />
        </div>
      ) : (
        <>
          {/* Cards below lg, a table from lg up. */}
          <ul className="grid gap-3 lg:hidden">
            {coupons.map((c) => (
              <CouponCard key={c.id} coupon={c} onEdit={() => setEditing(c)} onDelete={() => setDeleting(c)} />
            ))}
          </ul>
          <div className="card hidden overflow-x-auto lg:block">
            <table className="w-full">
              <thead className="border-b border-line bg-surface-muted/60">
                <tr>
                  <th className={th}>Code</th>
                  <th className={cn(th, 'text-right')}>Discount</th>
                  <th className={cn(th, 'text-right')}>You earn</th>
                  <th className={th}>Applies to</th>
                  <th className={th}>Status</th>
                  <th className={cn(th, 'text-right')}>Uses</th>
                  <th className={cn(th, 'text-right')}>Users</th>
                  <th className={cn(th, 'text-right')}>Earned</th>
                  <th className={cn(th, 'text-right')}>
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {coupons.map((c) => (
                  <tr key={c.id} className="hover:bg-surface-muted/50">
                    <td className={td}>
                      <div className="flex items-center gap-1">
                        <span className="num text-[14px] font-semibold">{c.code}</span>
                        <CopyButton text={c.code} label={`Copy ${c.code}`} toastTitle="Code copied" />
                      </div>
                      {c.note && <p className="max-w-[220px] truncate text-[12px] text-ink-muted">{c.note}</p>}
                    </td>
                    <td className={cn(td, 'num text-right')}>{pct(c.discountPct)}</td>
                    <td className={cn(td, 'num text-right font-semibold text-brand')}>{pct(c.commissionPct)}</td>
                    <td className={cn(td, 'text-ink-soft')}>{c.duration === 'forever' ? 'Every payment' : 'First payment'}</td>
                    <td className={td}>
                      <StatusControl coupon={c} />
                    </td>
                    <td className={cn(td, 'num text-right text-ink-soft')}>
                      {count(c.redemptions)}
                      {c.maxRedemptions != null && <span className="text-ink-faint"> / {count(c.maxRedemptions)}</span>}
                    </td>
                    <td className={cn(td, 'num text-right')}>{count(c.usersOnboarded)}</td>
                    <td className={cn(td, 'num text-right font-medium')}>{money(c.earnedCents)}</td>
                    <td className={cn(td, 'text-right')}>
                      <RowActions coupon={c} onEdit={() => setEditing(c)} onDelete={() => setDeleting(c)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 px-1 text-[12.5px] text-ink-faint">The discount and duration lock once a code is activated. To change the discount, create a new code.</p>
        </>
      )}

      <CouponDialog open={creating || Boolean(editing)} onClose={closeDialog} coupon={editing} maxPct={maxPct} tag={partner?.tag ?? ''} fallbackWord={fallbackWord} />
      <DeleteDialog coupon={deleting} onClose={() => setDeleting(null)} />
    </>
  )
}

/** Active ↔ inactive in one switch; a draft's switch activates it. */
function StatusControl({ coupon }: { coupon: Coupon }) {
  const activate = useCouponMutation(partnerApi.activateCoupon, { success: (c) => `${c ? c.code : 'Coupon'} is active` })
  const deactivate = useCouponMutation(partnerApi.deactivateCoupon, { success: (c) => `${c ? c.code : 'Coupon'} is paused` })
  const on = coupon.status === 'active'
  const busy = activate.isPending || deactivate.isPending
  const expired = coupon.expiresAt != null && new Date(coupon.expiresAt).getTime() < Date.now()
  return (
    <div className="flex items-center gap-2.5">
      <Switch checked={on} busy={busy} label={on ? `Deactivate ${coupon.code}` : `Activate ${coupon.code}`} onChange={(next) => (next ? activate.mutate(coupon.id) : deactivate.mutate(coupon.id))} />
      {expired ? <span className="text-[12px] font-medium text-ink-muted">Expired</span> : <Pill map={COUPON_STATUS} value={coupon.status} size="sm" />}
    </div>
  )
}

function RowActions({ coupon, onEdit, onDelete }: { coupon: Coupon; onEdit: () => void; onDelete: () => void }) {
  return (
    <div className="flex items-center justify-end gap-1">
      {coupon.shareUrl && (
        <CopyButton text={coupon.shareUrl} label="Copy share link" toastTitle="Share link copied" icon={<LinkIcon className="h-4 w-4" />} />
      )}
      <button type="button" onClick={onEdit} className="h-7 rounded-[7px] px-2 text-[12.5px] font-medium text-ink-soft hover:bg-surface-muted hover:text-ink">
        Edit
      </button>
      {coupon.status === 'draft' && coupon.redemptions === 0 && (
        <button type="button" onClick={onDelete} className="h-7 rounded-[7px] px-2 text-[12.5px] font-medium text-danger hover:bg-danger/5">
          Delete
        </button>
      )}
    </div>
  )
}

function CouponCard({ coupon: c, onEdit, onDelete }: { coupon: Coupon; onEdit: () => void; onDelete: () => void }) {
  return (
    <li className="card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1">
            <span className="num truncate text-[16px] font-semibold text-ink">{c.code}</span>
            <CopyButton text={c.code} label={`Copy ${c.code}`} toastTitle="Code copied" />
          </div>
          <p className="text-[13px] text-ink-muted">
            <span className="text-ink-soft">{pct(c.discountPct)} off</span> · you earn <span className="font-semibold text-brand">{pct(c.commissionPct)}</span> · {c.duration === 'forever' ? 'every payment' : 'first payment'}
          </p>
        </div>
        <StatusControl coupon={c} />
      </div>
      <dl className="mt-3 grid grid-cols-3 gap-2 rounded-field bg-surface-muted px-3 py-2.5">
        <Stat label="Uses" value={c.maxRedemptions != null ? `${count(c.redemptions)}/${count(c.maxRedemptions)}` : count(c.redemptions)} />
        <Stat label="Users" value={count(c.usersOnboarded)} />
        <Stat label="Earned" value={money(c.earnedCents)} />
      </dl>
      <div className="mt-3 flex items-center justify-between gap-2">
        <p className="min-w-0 truncate text-[12px] text-ink-faint">
          {c.note || (c.expiresAt ? `Expires ${date(c.expiresAt)}` : `Created ${date(c.createdAt)}`)}
        </p>
        <RowActions coupon={c} onEdit={onEdit} onDelete={onDelete} />
      </div>
    </li>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11.5px] text-ink-muted">{label}</dt>
      <dd className="num truncate text-[14px] font-semibold text-ink">{value}</dd>
    </div>
  )
}

function DeleteDialog({ coupon, onClose }: { coupon: Coupon | null; onClose: () => void }) {
  const remove = useCouponMutation(
    async (id: string) => {
      await partnerApi.deleteCoupon(id)
    },
    { success: () => 'Draft deleted' },
  )
  return (
    <Modal
      open={Boolean(coupon)}
      onClose={onClose}
      size="sm"
      title={`Delete ${coupon?.code ?? ''}?`}
      description="The draft is removed and the code becomes free for anyone to use."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Keep it
          </Button>
          <Button variant="danger" loading={remove.isPending} onClick={() => coupon && remove.mutate(coupon.id, { onSuccess: onClose })}>
            Delete
          </Button>
        </>
      }
    >
      <span />
    </Modal>
  )
}
